var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useEffect, useMemo } from 'react';
import { useAdConfigStorage } from '@ads/hooks/useAdConfigStorage';
import { useSession } from '@core/hooks/useSession';
import { logError } from '@core/logging';
import { AppContextType, } from '@embed/components/helpers';
import { getFWReferrerFromURL, isWidgetLive, uuidv4 } from '@embed/helpers';
import { useChannelStorage } from '@embed/hooks/useChannelStorage';
import { mapParamsToConfiguration } from '@embed/hooks/useMapParamsToConfiguration';
import { PublisherClientIdentificator, usePublisherClient, } from '@embed/hooks/usePublisherClient';
import { pruneNilValues } from '@embed/store/tools/utils';
import { customEvent, EventError, EventType, } from '@src/embeds/utils/customEvent';
import performanceFactory from '@src/embeds/utils/fwnPerformance';
import { useApi } from './useApi';
export var IPresentation;
(function (IPresentation) {
    IPresentation["CAROUSEL"] = "carousel";
    IPresentation["GRID"] = "grid";
    IPresentation["STORYBLOCK"] = "storyblock";
    IPresentation["STORYLINK"] = "storylink";
    IPresentation["FLOATING_BUTTON"] = "floating_button";
})(IPresentation || (IPresentation = {}));
const getPresentationFromParams = (appContextType, params) => {
    switch (appContextType) {
        case AppContextType.EMBED_STORYBLOCK_CONTEXT_TYPE:
            return IPresentation.STORYBLOCK;
        case AppContextType.EMBED_CHANNEL_BUTTON_CONTEXT_TYPE:
            return IPresentation.FLOATING_BUTTON;
        case AppContextType.EMBED_STORYLINK_CONTEXT_TYPE:
            return IPresentation.STORYLINK;
        case AppContextType.EMBED_CONTEXT_TYPE:
        case AppContextType.WIDGET_CONTEXT_TYPE: {
            switch (params.mode) {
                case 'grid':
                    return IPresentation.GRID;
                default:
                    return IPresentation.CAROUSEL;
            }
        }
        default:
            throw new Error('not defined presentation');
    }
};
export const useEmbedAppContext = ({ actions, params, appContextType, }) => {
    const publisherClient = usePublisherClient(PublisherClientIdentificator.APP_ID, params.app_id, params.target);
    const channel = useChannelStorage({
        username: params.channel,
        encodedId: params.channel_id,
    });
    const adConfig = useAdConfigStorage(params);
    const embedInstanceId = useMemo(uuidv4, []);
    const { session } = useSession();
    const api = useApi();
    const cohortsSerialized = useMemo(() => JSON.stringify(Object.entries(params)
        .filter(([key]) => key.startsWith('cohort_'))
        .sort()), [params]);
    useEffect(() => {
        ;
        (() => __awaiter(void 0, void 0, void 0, function* () {
            try {
                const cohortsEntries = JSON.parse(cohortsSerialized);
                if (cohortsEntries.length) {
                    const response = yield api.getPlaylistFromCohorts(channel === null || channel === void 0 ? void 0 : channel.username, Object.fromEntries(cohortsEntries));
                    actions.updateParams({ playlist: response.playlist_id });
                }
            }
            catch (err) {
                logError(err);
            }
        }))();
    }, [actions, cohortsSerialized, channel === null || channel === void 0 ? void 0 : channel.username, api]);
    return useMemo(() => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s;
        if ((params.app_id &&
            (!publisherClient || !isWidgetLive(params, publisherClient))) ||
            ((params.channel || params.channel_id) && !channel) ||
            !session ||
            typeof adConfig === 'undefined') {
            return null;
        }
        if (params.playlist && !(params.channel || params.channel_id)) {
            logError(new Error('Widget with `playlist` is missing `channel`'));
            return null;
        }
        const configuration = mapParamsToConfiguration(Object.assign(Object.assign({}, params), { max_videos: appContextType === AppContextType.WIDGET_CONTEXT_TYPE
                ? 0
                : params['max_videos'] }), publisherClient, channel);
        if (!configuration) {
            console.error('Invalid configuration of widget', {
                params,
                publisherClient,
            });
            params.target &&
                customEvent({
                    target: params.target,
                    type: [EventType.FW_ERROR, EventType.FW_EMBEDFEED_ERROR],
                    data: {
                        detail: EventError.API_ERROR,
                    },
                });
            return null;
        }
        const { embedFeedConfig, trackingData, trackingDataPixel } = configuration;
        const presentation = getPresentationFromParams(appContextType, params);
        const fwreferrer = getFWReferrerFromURL((_a = params.page_url) !== null && _a !== void 0 ? _a : '');
        if (typeof window !== 'undefined' && ((_b = window._fwn) === null || _b === void 0 ? void 0 : _b.analytics) && fwreferrer) {
            // Important to update fwreferrer from params.page_url in case widget is
            // being used in an iframe and access to window.location is limited.
            window._fwn.analytics.fwreferrer_origin = (_c = fwreferrer.origin) !== null && _c !== void 0 ? _c : '';
            window._fwn.analytics.fwreferrer_channel_id = (_d = fwreferrer.channel_id) !== null && _d !== void 0 ? _d : '';
            window._fwn.analytics.fwreferrer_video_id = (_e = fwreferrer.video_id) !== null && _e !== void 0 ? _e : '';
            window._fwn.analytics.fwreferrer_guest_id = (_f = fwreferrer.guest_id) !== null && _f !== void 0 ? _f : '';
        }
        const publisherClientId = publisherClient === null || publisherClient === void 0 ? void 0 : publisherClient.id;
        return {
            appContextType,
            params,
            username: (channel === null || channel === void 0 ? void 0 : channel.username) || params.channel || params.channel_id,
            publisherClientId,
            embedFeedConfig,
            embedInstanceId,
            guestId: session.guest_id,
            publisherClient,
            channel,
            adConfig,
            trackingData: Object.assign(Object.assign({ app_context_type: appContextType, embed_instance_id: embedInstanceId }, trackingData), { presentation, guest_id: session.guest_id, page_url: (_g = params.page_url) !== null && _g !== void 0 ? _g : '', product: 'embed.web.naboo' }),
            trackingDataPixel: pruneNilValues(Object.assign(Object.assign({ app_context_type: appContextType, embed_instance_id: embedInstanceId }, trackingDataPixel), { presentation, guest_id: session === null || session === void 0 ? void 0 : session.guest_id, page_url: params.page_url, product: 'embed.web.naboo', page_load_id: typeof window !== 'undefined' && ((_j = (_h = window._fwn) === null || _h === void 0 ? void 0 : _h.analytics) === null || _j === void 0 ? void 0 : _j.page_load_id), _referrer_video_id: typeof window !== 'undefined' &&
                    ((_l = (_k = window._fwn) === null || _k === void 0 ? void 0 : _k.analytics) === null || _l === void 0 ? void 0 : _l.fwreferrer_video_id), _referrer_channel_id: typeof window !== 'undefined'
                    ? (_o = (_m = window._fwn) === null || _m === void 0 ? void 0 : _m.analytics) === null || _o === void 0 ? void 0 : _o.fwreferrer_channel_id
                    : '', referrer_origin: typeof window !== 'undefined'
                    ? (_q = (_p = window._fwn) === null || _p === void 0 ? void 0 : _p.analytics) === null || _q === void 0 ? void 0 : _q.fwreferrer_origin
                    : '', referrer_guest_id: typeof window !== 'undefined'
                    ? (_s = (_r = window._fwn) === null || _r === void 0 ? void 0 : _r.analytics) === null || _s === void 0 ? void 0 : _s.fwreferrer_guest_id
                    : '' })),
            performance: performanceFactory(params),
        };
    }, [
        appContextType,
        channel,
        embedInstanceId,
        params,
        publisherClient,
        session,
        adConfig,
    ]);
};
